import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { formatDate, getFullName } from 'utils';
import { actions as dashboardActions } from 'features/dashboard';
import { Table, Tag, Tooltip } from 'antd';
import { useAsync } from 'react-async-hook';
import { commonService } from 'services';
import { ROLES } from 'constants';
import { selectUser } from 'features/auth';
import { DATE_FORMAT, EMDASH } from 'constants';
import { DashboardTableAssignedToCell } from '../AssignedToCell';
import { startCase } from 'lodash-es';
import { Chemistry, UserMultiple } from '@carbon/icons-react';

const PROVIDER_IMPORT_STATUS_MAP = {
  SUCCESS: 'Successful',
  FAILURE: 'Failed',
  DUPE_FOUND: 'Duplicate',
  IN_PROGRESS: 'In progress',
  RETRYABLE: 'Retryable',
};

export default function ProviderDashboardTable({
  state,
  rowKey,
  rowClassName,
  loading,
  hideSelection,
  onRow,
  mapper = (data) => data,
  className,
  sorterInfo,
  fetchData,
  ...rest
}) {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectUser);

  const rowSelection = {
    selectedRowKeys: state.selectedRowKeys ?? [],
    onChange: (keys) => {
      dispatch(dashboardActions.setSelectedRowKeys(keys));
    },
  };

  const data = useMemo(() => {
    if (mapper) {
      return (state.data || []).map(mapper);
    }
    return state.data || [];
  }, [mapper, state.data]);

  const { result: assigneeOptions = [] } = useAsync(async () => {
    return commonService.get(`/users/all?role=${ROLES.PROVIDER_COORDINATOR}`);
  }, []);

  const columns = useMemo(
    () => getProviderDashboardColumns({ assigneeOptions, currentUser, fetchData, sorterInfo }),
    [assigneeOptions, currentUser, fetchData, sorterInfo],
  );

  return (
    <Table
      {...rest}
      columns={columns}
      rowKey={rowKey}
      dataSource={data}
      loading={state.loading || loading}
      pagination={state.pagination}
      onChange={state.onChange}
      rowClassName={rowClassName}
      rowSelection={hideSelection ? undefined : rowSelection}
      expandable={{
        rowExpandable: () => false,
        showExpandColumn: false,
      }}
      onRow={onRow}
      showColSeparator={false}
      scroll={{ x: 'max-content' }}
      ref={rest.ref}
      locale={{
        filterReset: 'Clear',
        filterConfirm: 'Apply filters',
      }}
    />
  );
}

function getProviderDashboardColumns({ assigneeOptions, currentUser, fetchData, sorterInfo }) {
  const userFullName = [startCase(currentUser?.firstName), startCase(currentUser?.lastName)].join(' ');

  return [
    {
      title: 'App ID',
      dataIndex: 'id',
      key: 'applicationId',
      sorter: true,
      width: 140,
      render: (id, record) =>
        (
          <div className="flex items-center gap-2">
            <Link onClick={(e) => e.stopPropagation()} to={`/providers/applications/${id}`} className="underline">
              {id}
            </Link>

            {record?.careFacility?.organization?.testRecord ? (
              <Tooltip title="Test account">
                <Chemistry className="opacity-70" />
              </Tooltip>
            ) : (
              false
            )}
          </div>
        ) || EMDASH,
      sortOrder: sorterInfo?.columnKey === 'applicationId' && sorterInfo?.order,
    },
    {
      title: 'Name',
      dataIndex: 'careFacility.displayName',
      key: 'displayName',
      sorter: true,
      width: 200,
      className: 'truncate max-w-xs',
      sortOrder: sorterInfo?.columnKey === 'displayName' && sorterInfo?.order,
      render: (_, record) => record?.careFacility?.displayName,
    },
    {
      title: 'Location ID',
      dataIndex: 'careFacility.id',
      key: 'careFacilityId',
      width: 125,
      sorter: true,
      sortOrder: sorterInfo?.columnKey === 'careFacilityId' && sorterInfo?.order,
      render: (_, record) => record?.careFacility?.id,
    },
    {
      title: 'Program',
      dataIndex: 'programType.title',
      key: 'programType',
      sorter: true,
      width: 190,
      className: 'truncate max-w-xs',
      render: (_, record) => {
        const programName = record?.programType?.title;
        return programName ? <span title={programName}>{programName}</span> : 'N/A';
      },
      sortOrder: sorterInfo?.columnKey === 'programType' && sorterInfo?.order,
    },
    {
      title: 'Families',
      dataIndex: 'familyWaiting',
      key: 'familyWaiting',
      width: 160,
      render: (familyWaiting) =>
        familyWaiting ? (
          <Tag
            color="warning"
            icon={<UserMultiple size={11} className="mr-1 mb-[-1px]" />}
            className="border-none !bg-[#fef3c7] !text-[#92400e] py-1 px-3 rounded-2xl"
          >
            Family Waiting
          </Tag>
        ) : null,
    },
    {
      title: 'Status',
      headerText: 'Status',
      dataIndex: 'status.status.description',
      key: 'status',
      width: 160,
      sorter: true,
      className: 'truncate max-w-xs',
      render: (_, record) => {
        const status = record?.status?.status?.description;
        return status ? <span title={status}>{status}</span> : 'N/A';
      },
      sortOrder: sorterInfo?.columnKey === 'status' && sorterInfo?.order,
    },
    {
      title: 'Date',
      dataIndex: 'submittedDate',
      key: 'submittedDate',
      sorter: true,
      width: 180,
      render: (date) => <span>{formatDate(date, DATE_FORMAT[1], '')}</span>,
      sortOrder: sorterInfo?.columnKey === 'submittedDate' && sorterInfo?.order,
    },
    {
      title: 'Assigned to',
      width: 170,
      dataIndex: 'assignedTo',
      key: 'assignedTo',
      render: (_, record) => {
        return (
          <DashboardTableAssignedToCell
            options={[
              { text: userFullName, value: currentUser?.id },
              ...(assigneeOptions || [])
                .filter((assignee) => assignee.id !== currentUser?.id)
                .map((assignee) => ({ text: getFullName(assignee), value: assignee.id }))
                .sort((a, b) => a?.text?.localeCompare?.(b?.text)),
            ]}
            record={record}
            fetchData={fetchData}
          />
        );
      },
    },
    {
      title: 'Import Status',
      dataIndex: 'importStatus',
      key: 'importStatus',
      width: 160,
      render: (importStatus) =>
        importStatus ? PROVIDER_IMPORT_STATUS_MAP[importStatus] || startCase(importStatus.toLowerCase()) : null,
    },
    {
      title: 'Street',
      dataIndex: 'addresses.street',
      key: 'providerStreet',
      width: 200,
      sorter: true,
      className: 'truncate max-w-xs',
      render: (_, record) => {
        const value = record?.careFacility?.address?.street;
        return value ? (
          <p className="truncate max-w-xs" title={value}>
            {value}
          </p>
        ) : (
          'N/A'
        );
      },
      sortOrder: sorterInfo?.columnKey === 'providerStreet' && sorterInfo?.order,
    },
    {
      title: 'City',
      dataIndex: 'addresses.city',
      key: 'providerCity',
      width: 120,
      sorter: true,
      className: 'truncate max-w-xs',
      render: (_, record) => {
        const value = record?.careFacility?.address?.city;
        return value ? value : 'N/A';
      },
      sortOrder: sorterInfo?.columnKey === 'providerCity' && sorterInfo?.order,
    },
    {
      title: 'State',
      dataIndex: 'addresses.state',
      key: 'providerState',
      width: 90,
      sorter: true,
      className: 'truncate max-w-xs',
      render: (_, record) => {
        const value = record?.careFacility?.address?.state;
        return value ? value : 'N/A';
      },
      sortOrder: sorterInfo?.columnKey === 'providerState' && sorterInfo?.order,
    },
    {
      title: 'Zip',
      dataIndex: 'addresses.postalCode',
      key: 'providerZip',
      width: 90,
      sorter: true,
      className: 'truncate max-w-xs',
      render: (_, record) => {
        const value = record?.careFacility?.address?.postalCode;
        return value ? value : 'N/A';
      },
      sortOrder: sorterInfo?.columnKey === 'providerZip' && sorterInfo?.order,
    },
  ];
}
